
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import router from '@/router';
  import { useCartStore } from '@/stores/cart';
  import { useSeatmapStore } from '@/stores/seatmapStore';
  import { useToastStore } from '@/stores/toastStore';
import type { CartItem, CheckoutData, CheckoutEventState, CheckoutEventStateName, EventCheckoutData, OrderData, PaymentVars, StartPurchaseData, TicketCheckoutData, VouchercodeCheckoutData } from '@/types';
import { slugify } from '@/utils/slugify';
  import { startEpayPaymentWindow } from '@/utils/startEpayPaymentWindow';
import { startQuickPay10PaymentWindow } from '@/utils/startQuickPay10PaymentWindow';
import { getUrlParams } from '@/utils/urlParams';

export const eventCheckoutDataEmpty: EventCheckoutData = {
    info1: "",
    salestart: 0,
    saleend: 0,
    timezone: 'Europe/Copenhagen',
    eventtime: 0,
    venuename: "",
    venuemap_id: 0,
    maxtickets: 0,
    currency: "DKK",
    has_vouchercodes: 0,
    vat: 0,
    voucher_code_add_description: "",
    voucher_code_description: "",
    voucher_code_error_description: "",
    voucher_code_remove_description: "",
    voucher_code_valid_description: "",
    localizations: {},
    eventtime_gmt_offset: 0,
    eventtime_override: null,
    sale_is_over: false,
    photo1_id: ''
};

export const startPurchaseDataEmpty: StartPurchaseData = {
        countries: [],
        custom_fields: [],
        custom_fields_perticket: [],
        custom_fields_pertickettype: [],
        dankort_discount: null,
        doorsale_customerinfo: true,
        individual_ticket_photo: false,
        mailshipping: false,
        psp: "",
        psp_warning: "",
        require_address: true,
        require_city_zip: true,
        require_country: true,
        require_email: true,
        require_phone: true,
        require_name: true,
        fieldlists: {},
        single_sign_on_login_status: { status: "loggedout" },
        vouchercode_data: {},
        vouchercode_description: "",
        vouchercode_individual: false,
        has_newsletter: false,
        custom_newsletter: "",
};

export const orderDataEmpty: OrderData = {
        'email': '',
        'email2': '',
        'name': '',
        'address': '',
        'address2': '',
        'zip': '',
        'city': '',
        'telephone': '',
        'country': '',
        'newsletter': false,
        'customcheckbox': false,
        'vouchercodesavedata': false,
        'mailshipping': false,
        'giftcards': [],
        'price_group_info': undefined,
        'customfields_data': undefined
};

const vouchercodeDataEmpty: VouchercodeCheckoutData = {
    code: "",
    event_id: undefined,
    ok: 0,
};

//A store for all non-cart related checkout data

export const useCheckoutStore = defineStore('checkout', () => {
    const checkoutID = ref<number | string | undefined>(undefined);
    function setCheckoutID(id: number | string) {
        checkoutID.value = id;
        //save to local storage
        
        localStorage.setItem('checkoutID', id.toString());
    }
    function getCheckoutID() {
        if (!checkoutID.value) {
            // try to load from local storage
            const storedID = localStorage.getItem('checkoutID');
            if (storedID) {
                // If storedID is a number, parse it; if it's a string (closed event), use as is
                if (!isNaN(Number(storedID))) {
                    checkoutID.value = Number(storedID);
                } else {
                    checkoutID.value = storedID;
                }
            }
        }
        return checkoutID.value;
    }


    const id = ref<number | string | undefined>(undefined);
    const event = ref<EventCheckoutData>(eventCheckoutDataEmpty);
    const eventState = ref<CheckoutEventStateName>('loading');
    const eventStateData = ref<CheckoutEventState | null>(null);
    const eventServerTime = ref<number | null>(null);
    const tickets = ref<TicketCheckoutData[]>([]);
    const noticeTickets = ref<TicketCheckoutData[]>([]);
    const startPurchaseData = ref<StartPurchaseData>(startPurchaseDataEmpty);
    const orderData = ref<OrderData>(orderDataEmpty);
    const paymentModal = ref<boolean>(false);
    const orderErrors = ref<string[]>([]);
    const customFieldErrors = ref<string[]>([]);
    const isPlacingOrder = ref<boolean>(false);
    const paymentBegun = ref<boolean>(false);
    const showCancelPaymentModal = ref<boolean>(false);
    const bypassPaymentGuard = ref<boolean>(false);
    const placeOrderPaymentVars = ref<PaymentVars | null>(null);
    const vouchercode = ref<VouchercodeCheckoutData>(vouchercodeDataEmpty);
    const vouchercodeDataSet = ref<boolean>(false);
    const currentLanguage = ref<string>('da');
    const urlParams = ref<Record<string, string>>({});

    // Invoice payment (IVP/EAN) state
    const ivpAvailable = ref<boolean>(false);
    const ivpEmailRequired = ref<boolean>(false);
    const ivpEanRequired = ref<boolean>(false);
    const ivpMode = ref<boolean>(false);
    const ivpFormData = ref({
        ivp_name: '',
        ivp_attperson: '',
        ivp_address: '',
        ivp_zip: '',
        ivp_city: '',
        ivp_email: '',
        ivp_email2: '',
        ivp_ean: '',
        ivp_cvr: '',
        ivp_reference: '',
        ivp_toa: false,
    });

    const { locale } = useI18n();
    const { t: $t } = useI18n();

    watch(locale, (newLocale) => {
        currentLanguage.value = newLocale;
    });
    const originalDescriptions = {
        voucher_code_add_description: "",
        voucher_code_description: "",
        voucher_code_error_description: "",
        voucher_code_remove_description: "",
        voucher_code_valid_description: "",
    };
    const toastStore = useToastStore();

    function isExpectedEvent(id: number | string): boolean {
        const expectedID = getCheckoutID();
        if (expectedID === undefined) return false;
        return String(id) === String(expectedID);
    }

    function setCheckoutData(data: CheckoutData) {
        if (!isExpectedEvent(data.id)) return;

        eventState.value = 'available';
        eventStateData.value = null;
        eventServerTime.value = data.server_time;
        //First clear noticeTickets, then compare tickets in data.tickets to see if any are missing in current tickets
        noticeTickets.value = [];
        data.tickets.forEach(ticket => {
            const matchingTicket = tickets.value.find(t => t.id === ticket.id);
            if (!matchingTicket) {
                noticeTickets.value.push(ticket);
            }
        });
        id.value = data.id;
        event.value = data.event;
        tickets.value = data.tickets;
        vouchercode.value = data.vouchercode || vouchercodeDataEmpty;
        //When setting checkout data, also set event descriptions according to current language
        currentLanguage.value = locale.value;
        setEventDescriptionsFromLanguage(true, currentLanguage.value);

        // Register this event as having seatmaps if any ticket type uses one
        if (data.id != null && data.tickets.some(t => t.seatmap)) {
            useSeatmapStore().registerEventWithSeatmaps(data.id);
        }
        // Sale ended while the customer was in checkout: bounce them to the event page.
        if (data.event.sale_is_over && router.currentRoute.value.name != 'event-detail') {
            toastStore.showToast($t('checkout.sale_is_over'));
            router.push({ name: 'event-detail', params: { id: data.id, name: slugify(data.event.info1) } });
            // resend=false: no point re-subscribing/reserving tickets for an event whose sale has ended.
            useCartStore().removeFromCart(data.id, false);
            event.value.sale_is_over = false;
        }
        // console.log("Checkout data set:", data);
        // console.log("Event data:", event);
        // console.log("Tickets data:", tickets);
        // console.log("Vouchercode:", vouchercode);
    }

    function setCheckoutEventState(data: CheckoutEventState) {
        if (!isExpectedEvent(data.id)) return;

        eventState.value = data.state;
        eventStateData.value = data;
        eventServerTime.value = data.server_time;
        event.value = eventCheckoutDataEmpty;
        tickets.value = [];
        noticeTickets.value = [];
    }

    function setCheckoutEventLoading() {
        eventState.value = 'loading';
        eventStateData.value = null;
        eventServerTime.value = null;
    }
    //Whenever the language changes, we need to update the vouchercode descriptions in event data with those its localized strings
    //This is done in checkout page component, which watches the language change and calls this function
    //Maybe we need to do this from the locales plugin instead?
    function setEventDescriptionsFromLanguage(fromSetCheckoutData: boolean = false, lang: string = "") {
        const localizationData = event.value.localizations?.[lang];
        if(fromSetCheckoutData) {
            //Store original descriptions from event data when setting checkout data
            originalDescriptions.voucher_code_add_description = event.value.voucher_code_add_description;
            originalDescriptions.voucher_code_description = event.value.voucher_code_description;
            originalDescriptions.voucher_code_error_description = event.value.voucher_code_error_description;
            originalDescriptions.voucher_code_remove_description = event.value.voucher_code_remove_description;
            originalDescriptions.voucher_code_valid_description = event.value.voucher_code_valid_description;
        }
        if (localizationData) {
            if (localizationData.voucher_code_add_description) {
                event.value.voucher_code_add_description = localizationData.voucher_code_add_description;
            }
            if (localizationData.voucher_code_description) {
                event.value.voucher_code_description = localizationData.voucher_code_description;
            }
            if (localizationData.voucher_code_error_description) {
                event.value.voucher_code_error_description = localizationData.voucher_code_error_description;
            }
            if (localizationData.voucher_code_remove_description) {
                event.value.voucher_code_remove_description = localizationData.voucher_code_remove_description;
            }
            if (localizationData.voucher_code_valid_description) {
                event.value.voucher_code_valid_description = localizationData.voucher_code_valid_description;
            }
        }
        else if(!fromSetCheckoutData) {
            //Revert to original descriptions if no localization data for current language
            event.value.voucher_code_add_description = originalDescriptions.voucher_code_add_description;
            event.value.voucher_code_description = originalDescriptions.voucher_code_description;
            event.value.voucher_code_error_description = originalDescriptions.voucher_code_error_description;
            event.value.voucher_code_remove_description = originalDescriptions.voucher_code_remove_description;
            event.value.voucher_code_valid_description = originalDescriptions.voucher_code_valid_description;
        }
    }


    function setStartPurchaseData(data: StartPurchaseData) {
        startPurchaseData.value = data;

    }

    function startQuickPay10Payment() {
        paymentBegun.value = true;
        startQuickPay10PaymentWindow(placeOrderPaymentVars.value);
    }

    function restartEpayPayment(psp: string, paymentVars: PaymentVars) {
        if(psp && psp == 'epay' && paymentVars) {
            startPurchaseData.value.psp = 'epay';
            placeOrderPaymentVars.value = paymentVars;
            startEpayPayment();
        }
    }

    function startEpayPayment() {
        // If psp is epay, we need to set up for starting ePay payment later
        // Only if in information view (payment not yet begun)
        if (startPurchaseData.value.psp == 'epay' && placeOrderPaymentVars.value) {
           paymentBegun.value = true;
           startEpayPaymentWindow(placeOrderPaymentVars.value);
        }
    }

    function setOrderData(data: OrderData) {
        orderData.value = data;
        console.log("Order data set:", data);
    }

    function setVouchercodeData(data: VouchercodeCheckoutData) {
        vouchercode.value = data;
        if(data.code && data.ok == 1 && data.code.length > 0) {
            toastStore.showToast(event.value.voucher_code_valid_description || "Vouchercode applied successfully.");
        }
        else if (data.ok == 1) {
            toastStore.showToast(event.value.voucher_code_description ? $t('checkout.vouchercode_removed', { voucher_code_description: event.value.voucher_code_description }) : "Vouchercode removed.");
        }
        else {
            toastStore.showToast(event.value.voucher_code_error_description || "Vouchercode invalid.");
        }
    }

    function clearCheckoutData() {
        id.value = undefined;
        event.value = eventCheckoutDataEmpty;
        tickets.value = [];
        vouchercode.value = vouchercodeDataEmpty;
        startPurchaseData.value = startPurchaseDataEmpty;
        ivpAvailable.value = false;
        ivpEmailRequired.value = false;
        ivpEanRequired.value = false;
        ivpMode.value = false;
        ivpFormData.value = {
            ivp_name: '',
            ivp_attperson: '',
            ivp_address: '',
            ivp_zip: '',
            ivp_city: '',
            ivp_email: '',
            ivp_email2: '',
            ivp_ean: '',
            ivp_cvr: '',
            ivp_reference: '',
            ivp_toa: false,
        };
    }

    function getCheckoutLanguage(){
        return currentLanguage.value;
    }

    function loadUrlParams() {
        urlParams.value = getUrlParams();
    }

    function getUrlParam(key: string): string | undefined {
        return urlParams.value[key];
    }
    
    // Getter for tickets that are not upsale tickets (upsale_flag != true)
    const displayTickets = computed<TicketCheckoutData[]>(() => tickets.value.filter(ticket => !ticket.upsale_flag));

    // Getter for tickets that are upsale tickets (upsale_flag == true)
    const upsaleTickets = computed<TicketCheckoutData[]>(() => tickets.value.filter(ticket => ticket.upsale_flag));

    // TODO move this. Also hasUpsaleTickets should only be true 
    // Getter: returns true if there are any upsale tickets for the current event
    const hasUpsaleTickets = computed<boolean>(() => tickets.value.some(ticket => ticket.upsale_flag));

    function exportAsDummyCartItem(): CartItem {
        const dummyCartItem: CartItem = {
            id: id.value || 0,
            name: event.value.info1,
            currency: event.value.currency || 'DKK',
            vat: { // TODO
                cardfee: 25,
                ticketprice: 25,
                shippingfee: 25,
                ticketfee: 25, 
            }, 
            giftcard_allowed: false,
            localizations: event.value.localizations || {},
            eventtime: event.value.eventtime || 0,
            eventtime_gmt_offset: event.value.eventtime_gmt_offset || 0,
            eventtime_override: event.value.eventtime_override || null,
            venueName: event.value.venuename,
            photo1_id: event.value.photo1_id || '',
            tickets: [],
            upsaleTickets: [],
        };
        return dummyCartItem;
    }

    return { id, checkoutID, event, eventState, eventStateData, eventServerTime, tickets, displayTickets, upsaleTickets: upsaleTickets, hasUpsaleTickets, vouchercode, noticeTickets, startPurchaseData, paymentModal, orderErrors, customFieldErrors, isPlacingOrder, orderData, paymentBegun, showCancelPaymentModal, bypassPaymentGuard, placeOrderPaymentVars, vouchercodeDataSet, urlParams, ivpAvailable, ivpEmailRequired, ivpEanRequired, ivpMode, ivpFormData, setOrderData, setStartPurchaseData, setCheckoutData, setCheckoutEventState, setCheckoutEventLoading, clearCheckoutData, getCheckoutID, setCheckoutID, setVouchercodeData, setEventDescriptionsFromLanguage, startEpayPayment, restartEpayPayment, startQuickPay10Payment, getCheckoutLanguage, loadUrlParams, getUrlParam, exportAsDummyCartItem };

});
